import React from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBox,
  faBroadcastTower,
  faCheck,
  faNewspaper,
  faPencil,
  faShareAlt,
  faStore,
  faTruck,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Grid, HoverHintWrapper, Link, StatusBadge,
} from '@domain-group/suit-theme'

import useTemplatePage from '@hooks/useTemplatePage'

import type { TemplatePageModel } from '@domain-group/realhub-backend-api-types'

import PagePreview from '@components/PagePreview'

import PermissionIcon from './PermissionIcon'

const renderStatusBadge = (templatePage: Partial<TemplatePageModel>) => {
  const { hidden, production_ready } = templatePage

  const boxProps = {
    marginRight: 'small',
  }

  if (hidden){
    return (
      <StatusBadge
        boxProps={boxProps}
        color="red"
        // @ts-expect-error TS2322
        text={(
          <Box as="span">
            Archived
          </Box>
        )}
      />
    )
  }

  if (production_ready){
    return (
      <StatusBadge
        boxProps={boxProps}
        color="blue"
        // @ts-expect-error TS2322
        text={(
          <Box as="span">
            <FontAwesomeIcon icon={faBroadcastTower} style={{ marginRight: 4 }} /> Live
          </Box>
        )}
      />
    )
  }

  return (
    <StatusBadge
      boxProps={boxProps}
      color="lightGrey"
      // @ts-expect-error TS2322
      text={(
        <Box as="span">
          <FontAwesomeIcon icon={faPencil} style={{ marginRight: 4 }} /> Draft
        </Box>
      )}
    />
  )
}

const renderInUseBadge = (artworksInUseCount: number) => (
  <StatusBadge
    boxProps={{ marginLeft: 'auto' }}
    color="lightGrey"
    // @ts-expect-error TS2322
    text={(
      <Box as="span">
        In use: {artworksInUseCount}
      </Box>
    )}
  />
)

interface TemplatePageGridTileProps {
  onToggleSelected?: (id: number) => void,
  templatePage: TemplatePageModel,
  setSelectedTemplatePage: (template: TemplatePageModel) => void,
  selectedId?: boolean,
}

const TemplatePageGridTile = (props: TemplatePageGridTileProps) => {
  const {
    onToggleSelected,
    templatePage: initTemplatePage,
    setSelectedTemplatePage,
    selectedId,
  } = props

  const templatePagePayload = useTemplatePage(initTemplatePage)
  const {
    callbacks: {
      assignTemplatePagePermissions,
    },
    hasOriginTemplatePage,
    permissionCounts,
    templatePage,
    templatePage: {
      id, preview_image, title,
    },
  } = templatePagePayload

  const artworksInUseCount = Number(
    templatePage?.in_use_count ?? 0,
  )
  const templatePageId = initTemplatePage?.id

  const previewOverlayColor = 'rgba(0, 0, 0, 0.5)'

  return (
    <Box flexDirection="column">
      <Box
        onClick={() => {
          if (typeof templatePageId === 'number' && onToggleSelected){
            onToggleSelected(templatePageId)
            return
          }

          setSelectedTemplatePage(initTemplatePage)
        }}
        alignItems="center"
        backgroundColor="whiteGrey"
        border="1px solid"
        borderColor="lineColor"
        borderRadius={5}
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        height={190}
        justifyContent="center"
        position="relative"
      >
        <PagePreview
          holderHeight={190}
          holderPadding={16}
          holderWidth={240}
          imageHeight={templatePage?.dimensions?.pixels?.height ?? 0}
          imageUrl={preview_image}
          imageWidth={templatePage?.dimensions?.pixels?.width ?? 0}
          style={{
            backgroundColor: 'white',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            cursor: 'pointer',
          }}
        />

        {selectedId && (
          <>
            <Box
              backgroundColor={previewOverlayColor}
              borderRadius={5}
              borderBottomLeftRadius={0}
              borderBottomRightRadius={0}
              height="100%"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                if (typeof templatePageId === 'number' && onToggleSelected){
                  onToggleSelected(templatePageId)
                }
              }}
              left={0}
              position="absolute"
              top={0}
              width="100%"
            />

            <Box
              alignItems="center"
              backgroundColor="blue"
              border="2px solid white"
              borderRadius="100%"
              color="white"
              height="40px"
              justifyContent="center"
              left="50%"
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                if (typeof templatePageId === 'number' && onToggleSelected){
                  onToggleSelected(templatePageId)
                }
              }}
              position="absolute"
              top="50%"
              width="40px"
              style={{ cursor: onToggleSelected ? 'pointer' : 'default', transform: 'translate(-50%, -50%)' }}
            >
              <FontAwesomeIcon icon={faCheck} />
            </Box>
          </>
        )}
      </Box>

      <Box
        border="1px solid"
        borderColor="lineColor"
        borderTop={0}
        borderBottomLeftRadius={5}
        borderBottomRightRadius={5}
        flexDirection="column"
      >
        <Box alignItems="center" borderBottom="1px solid" borderColor="lineColor" padding="medium">
          {renderStatusBadge(initTemplatePage)}
          {renderInUseBadge(artworksInUseCount)}
        </Box>

        <Box alignItems="center" borderBottom="1px solid" borderColor="lineColor" padding="large">
          <Box flexDirection="column" width="100%">
            <HoverHintWrapper
              boxProps={{ display: 'inline-block', position: 'relative', width: '100%' }}
              childrenHolderProps={{ color: 'bodyFontLightColor' }}
              hintComponent={<HoverHintWrapper.Hint>{title}</HoverHintWrapper.Hint>}
            >
              <Link
                textProps={{
                  color: 'bodyFontColor',
                  fontSize: 'small',
                  fontWeight: 'bold',
                  variant: 'ellipsis',
                }}
                onClick={() => { setSelectedTemplatePage(initTemplatePage) }}
              >
                {hasOriginTemplatePage && <FontAwesomeIcon icon={faShareAlt} style={{ marginRight: 8 }} />}

                {title}
              </Link>
            </HoverHintWrapper>

            <Box marginTop="-6px">
              <Link
                onClick={() => { setSelectedTemplatePage(initTemplatePage) }}
                textProps={{
                  color: 'bodyFontLightColor',
                  fontSize: 'xsmall',
                }}
              >
                Template #{id}
              </Link>
            </Box>
          </Box>
        </Box>

        {/*
         // @ts-expect-error TS2322 */}
        <Grid gridAutoRows="1fr" gridTemplateColumns="repeat(4, minmax(25%, 1fr))">
          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('providerIds') }}
            icon={<FontAwesomeIcon icon={faTruck} />}
            label="Providers"
            value={permissionCounts.providers}
          />

          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('serviceProductIds') }}
            icon={<FontAwesomeIcon icon={faBox} />}
            label="Products"
            value={permissionCounts.products}
          />

          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('publicationIds') }}
            icon={<FontAwesomeIcon icon={faNewspaper} />}
            label="Publications"
            value={permissionCounts.publications}
          />

          <PermissionIcon
            callbacks={{ onClick: () => assignTemplatePagePermissions('agencyIds') }}
            icon={<FontAwesomeIcon icon={faStore} />}
            label="Agencies"
            value={permissionCounts.agencies}
          />
        </Grid>
      </Box>
    </Box>
  )
}

export default TemplatePageGridTile
